import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cx, focusRingClass, interactiveClass } from './shared';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'children'> {
  label: string;
  children: ReactNode;
  variant?: 'neutral' | 'accent' | 'danger';
}

const variantClass = {
  neutral:
    'border-border-subtle bg-surface text-ink hover:border-border-strong hover:bg-surface-recessed',
  accent:
    'border-accent bg-accent-soft text-accent-ink hover:border-accent-hover hover:bg-surface',
  danger:
    'border-status-failed-border bg-status-failed-surface text-status-failed hover:border-action-danger',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, children, variant = 'neutral', className, type = 'button', ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        focusRingClass,
        interactiveClass,
        'inline-flex h-control-comfortable w-control-comfortable shrink-0 items-center justify-center rounded-control border disabled:cursor-not-allowed disabled:border-border-strong disabled:bg-surface-recessed disabled:text-ink-muted',
        variantClass[variant],
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true">{children}</span>
    </button>
  ),
);

IconButton.displayName = 'IconButton';
